import { DefinedInitialDataOptions, useQuery } from "@tanstack/react-query";
import { FindSlotGamesResponse } from "types/slots/dto";
import { useAppContext } from "contexts";

export enum GameUsageTimeRange {
  ONE_HOUR = "one_hour",
  ONE_DAY = "one_day",
  ONE_WEEK = "one_week",
  ONE_MONTH = "one_month",
  ALL_TIME = "all_time",
}

export enum FindSlotGamesSort {
  NAME_ASC = "name_asc",
  NAME_DESC = "name_desc",
  MOST_POPULAR = "most_popular",
  PROVIDER = "provider",
  NEW_RELEASES = "new_releases",
}

type useSlotGamesProps = {
  take?: number;
  skip?: number;
  search?: string;
  sort?: FindSlotGamesSort | string;
  provider?: string;
  enabled?: boolean;
  isFavorite?: boolean;
  isHot?: boolean;
  isChallenge?: boolean;
  challengeId?: string | number;
  code?: string;
  name?: string;
  queryOptions?: DefinedInitialDataOptions<
    FindSlotGamesResponse,
    Error,
    FindSlotGamesResponse,
    readonly unknown[]
  >;
};

const useSlotGames = ({
  take = 5,
  skip = 0,
  search,
  provider,
  sort,
  code,
  name,
  isHot,
  enabled = true,
  isFavorite = false,
  isChallenge = false,
  queryOptions,
  challengeId,
}: useSlotGamesProps) => {
  const { apiService } = useAppContext();
  return useQuery<FindSlotGamesResponse>({
    queryKey: ["slot-games", search, skip, take, provider, sort, isHot, isChallenge],
    queryFn: async () => {
      const searchFilter = `${!!search ? `&search=${search}` : ""}`;
      const providerFilter = `${!!provider ? `&provider=${provider}` : ""}`;
      const sortFilter = `${!!sort ? `&sort=${sort}` : ""}`;
      const favorite = isFavorite ? "/favorite" : "";
      const columnFilter = code ? `&code=${code}` : name ? `&name=${name}` : "";
      const isHotFilter = isHot ? `&isHot=${isHot}` : "";
      const challenge = isChallenge && !favorite ? `/challenges` : "";
      const challengeFilter = isChallenge && challengeId ? `&challengeId=${challengeId}` : "";

      const response = await apiService.request(
        `/slots${favorite}${challenge}?skip=${skip}&take=${take}${searchFilter}${columnFilter}${providerFilter}${sortFilter}${isHotFilter}${challengeFilter}`,
        {
          method: "GET",
        }
      );

      const data = await response?.json();

      if (!response?.ok) {
        return Promise.reject([]);
      }

      return data;
    },
    initialData: { data: [], total: 0 },
    placeholderData: () => ({ data: [], total: 0 }),
    enabled,
    ...(queryOptions || {}),
  });
};

export default useSlotGames;
